import { useEffect, useState } from "react";
import { Button, Checkbox, Input, Textarea } from "@legal-500/legal500-design-system";
import {
  powerlistAnswers,
  powerlistEditionQuestions,
  type EditionQuestion,
  type EntryAnswer,
} from "../../api/powerlist.service";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { useQuery } from "../../hooks/useApi";
import { FormError, errorMessage, parseSortOrder, usePowerlistAccess } from "./shared";
import styles from "./Powerlist.module.css";

type AnswerEditorProps = {
  entryId: string;
  question: EditionQuestion;
  saved: EntryAnswer | undefined;
  defaultOrder: number;
  onSaved: (message: string) => Promise<void>;
};

function AnswerEditor({ entryId, question, saved, defaultOrder, onSaved }: AnswerEditorProps) {
  const { canWrite, canDelete } = usePowerlistAccess();
  const [answer, setAnswer] = useState(saved?.answer ?? "");
  const [visible, setVisible] = useState(saved?.isVisible ?? true);
  const [order, setOrder] = useState(String(saved?.sortOrder ?? defaultOrder));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAnswer(saved?.answer ?? "");
    setVisible(saved?.isVisible ?? true);
    setOrder(String(saved?.sortOrder ?? defaultOrder));
  }, [saved, defaultOrder]);

  async function save(isVisible: boolean, message: string) {
    const text = answer.trim() || saved?.answer || "";
    const sortOrder = parseSortOrder(order);
    if (!text) {
      setError("Enter an answer first");
      return;
    }
    if (sortOrder === "invalid") {
      setError("Order must be a whole number of 0 or more");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await powerlistAnswers.upsert(entryId, question.questionId, {
        answer: text,
        isVisible,
        sortOrder,
      });
      await onSaved(message);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await powerlistAnswers.remove(entryId, question.questionId);
      await onSaved("Answer removed");
    } catch (removeError) {
      setError(errorMessage(removeError));
    } finally {
      setBusy(false);
    }
  }

  function toggleVisible(next: boolean) {
    setVisible(next);
    if (saved)
      void save(next, next ? "Answer shown publicly" : "Answer hidden from the public site");
  }

  const headingId = `powerlist-question-${question.questionId}`;

  return (
    <section className={styles.answer} aria-labelledby={headingId}>
      <div className={styles.answerHeader}>
        <h3 id={headingId} className={styles.answerQuestion}>
          {question.text}
        </h3>
        {question.isMandatory && <span className={styles.tag}>Mandatory</span>}
        {saved && !saved.isVisible && (
          <span className={`${styles.tag} ${styles.tagWarning}`}>Hidden</span>
        )}
        {!saved && <span className={styles.tag}>Not answered</span>}
      </div>
      <FormError message={error} />
      <Textarea
        id={`${headingId}-answer`}
        label="Answer"
        value={answer}
        onChange={event => setAnswer(event.target.value)}
        rows={4}
        disabled={!canWrite || busy}
        fullWidth
      />
      <div className={styles.answerControls}>
        <Checkbox
          label="Visible on the public site"
          aria-label={`Visible on the public site: ${question.text}`}
          checked={visible}
          disabled={!canWrite || busy}
          onChange={event => toggleVisible(event.target.checked)}
        />
        <Input
          id={`${headingId}-order`}
          className={styles.orderInput}
          label="Order"
          type="number"
          min={0}
          inputMode="numeric"
          value={order}
          onChange={event => setOrder(event.target.value)}
          disabled={!canWrite || busy}
        />
        {canWrite && (
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            aria-label={`Save answer: ${question.text}`}
            onClick={() => void save(visible, "Answer saved")}
          >
            Save answer
          </Button>
        )}
        {canDelete && saved && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={busy}
            aria-label={`Remove answer: ${question.text}`}
            onClick={() => void remove()}
          >
            Remove answer
          </Button>
        )}
      </div>
    </section>
  );
}

type EntryAnswersSectionProps = {
  editionId: string;
  entryId: string;
  onMessage: (message: string) => void;
};

export function EntryAnswersSection({ editionId, entryId, onMessage }: EntryAnswersSectionProps) {
  const { data, loading, error, refetch, isRefetching } = useQuery(async () => {
    const [questions, answers] = await Promise.all([
      powerlistEditionQuestions.list(editionId),
      powerlistAnswers.list(entryId),
    ]);
    return { questions, answers };
  }, [editionId, entryId]);

  const questions = [...(data?.questions ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const answers = new Map((data?.answers ?? []).map(answer => [answer.questionId, answer]));

  return (
    <section className={styles.section} aria-labelledby="powerlist-answers-heading">
      <h2 id="powerlist-answers-heading" className={styles.sectionTitle}>
        Interview answers
      </h2>
      <p className={styles.muted}>
        Hidden answers are kept here but never shown on the public interview page.
      </p>
      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
      {loading && data === null ? (
        <LoadingState message="Loading answers..." />
      ) : questions.length === 0 ? (
        <p className={styles.muted}>This edition asks no questions yet.</p>
      ) : (
        questions.map((question, index) => (
          <AnswerEditor
            key={question.questionId}
            entryId={entryId}
            question={question}
            saved={answers.get(question.questionId)}
            defaultOrder={index}
            onSaved={async message => {
              onMessage(message);
              await refetch();
            }}
          />
        ))
      )}
    </section>
  );
}
